import { notifyFitnessChanged } from "./fitnessChanges";
import { fitnessId, fitnessActionId, isFitnessWriteAction } from "../../supabase/functions/_shared/fitnessActionDomain";
import { notifyBodyHealthChanged } from "./bodyHealthChanges";
import { bodyHealthId, bodyHealthActionId } from "../../supabase/functions/_shared/bodyHealthActionDomain";
import { publishAgendaRefresh } from "./agendaChanges";
import { agendaId, agendaActionId, isAgendaWriteAction } from "../../supabase/functions/_shared/agendaActionDomain";
import type { Task } from "@/hooks/useTasks";
import { publishTaskChange, type TaskChange } from "./taskChanges";
import { publishHabitRefresh as notifyHabitsChanged } from "./habitChanges";
import { habitActionId, habitId, isHabitWriteAction } from "../../supabase/functions/_shared/habitActionDomain";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PRIORITIES = new Set(["Critical", "Important", "Normal"]);
const LOCAL_SOURCES = new Set<unknown>([null, "manual", "mirror", "voice"]);
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const nullableString = (value: unknown) => value === null || typeof value === "string";
const timestamp = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value));

export interface MirrorActionRequest {
  userId: string;
  sessionId: string;
  authEpoch: number;
  request: number;
}
export function invalidateMirrorActionStream(state: { request: number; controller: AbortController | null }): void {
  state.request++;
  state.controller?.abort();
  state.controller = null;
}
export function isCurrentMirrorActionRequest(
  sent: MirrorActionRequest,
  current: Omit<MirrorActionRequest, "userId" | "sessionId"> & {
    userId: string | null; sessionId: string | null; mounted: boolean;
  },
): boolean {
  return current.mounted && sent.userId === current.userId && sent.sessionId === current.sessionId &&
    sent.authEpoch === current.authEpoch && sent.request === current.request;
}

/** Only confirmed database outcomes refresh module state. Proposals, provider
 * actions, and future device commands cannot pretend to be a local task write.
 */
export function confirmedMirrorTaskChanges(results: unknown, userId: string): TaskChange[] {
  if (!Array.isArray(results) || !UUID.test(userId)) return [];
  const changes: TaskChange[] = [];
  const seen = new Set<string>();
  for (const result of results) {
    if (!record(result) || result.ok !== true || result.operation !== "update_vyv_task" ||
      result.scope !== "local" || result.evidence !== "database_readback" ||
      typeof result.action_id !== "string" || !result.action_id.trim() || result.action_id.length > 256 ||
      seen.has(result.action_id) || !record(result.task)) continue;
    const task = result.task;
    if (typeof task.id !== "string" || !UUID.test(task.id) || task.user_id !== userId ||
      typeof task.title !== "string" || !task.title.trim() ||
      typeof task.priority !== "string" || !PRIORITIES.has(task.priority) ||
      typeof task.done !== "boolean" || (task.created_at !== null && !timestamp(task.created_at)) ||
      !nullableString(task.due_date) || (task.due_date !== null && !timestamp(task.due_date)) ||
      !nullableString(task.time_label) ||
      (task.completed_at !== undefined && task.completed_at !== null && !timestamp(task.completed_at)) ||
      !LOCAL_SOURCES.has(task.source) ||
      (task.external_id !== undefined && !nullableString(task.external_id))) continue;
    seen.add(result.action_id);
    // Keep the module event shape canonical rather than spreading an arbitrary
    // server payload into shared state.
    const confirmed: Task = {
      id: task.id, user_id: userId, title: task.title,
      priority: task.priority as Task["priority"], done: task.done,
      created_at: task.created_at as string,
      due_date: task.due_date as string | null, time_label: task.time_label as string | null,
      ...(task.completed_at !== undefined ? { completed_at: task.completed_at as string | null } : {}),
      ...(task.source !== undefined ? { source: task.source as string | null } : {}),
      ...(task.external_id !== undefined ? { external_id: task.external_id as string | null } : {}),
    };
    changes.push({ userId, id: confirmed.id, task: confirmed });
  }
  return changes;
}

export function publishMirrorActionResults(
  results: unknown,
  context: { userId: string; sessionId?: string; isCurrent: () => boolean },
  publish: (change: TaskChange) => void = publishTaskChange,
  publishHabit: (userId: string) => void = notifyHabitsChanged,
  publishAgenda: (userId: string) => void = publishAgendaRefresh,
): number {
  let published = 0;
  for (const change of confirmedMirrorTaskChanges(results, context.userId)) {
    // A subscriber may cause navigation or an account change. Recheck before
    // each event instead of accepting the rest of an obsolete batch.
    if (!context.isCurrent()) break;
    publish(change); published++;
  }
  // A verified receipt invalidates current owned reads. It never applies a
  // historical habit/history payload or rewards XP in the client.
  const seen = new Set<string>();
  if (Array.isArray(results) && UUID.test(context.userId) && typeof context.sessionId === "string" && UUID.test(context.sessionId)) for (const result of results) {
    if (!context.isCurrent()) break;
    if (!record(result) || result.ok !== true || result.operation !== "verify_habit_receipt" || result.scope !== "local" || result.evidence !== "habit_action_receipt"
      || result.confirmed !== true || result.status !== "succeeded" || result.user_id !== context.userId || result.session_id !== context.sessionId
      || typeof result.action !== "string" || !isHabitWriteAction(result.action) || result.unchanged !== undefined && typeof result.unchanged !== "boolean"
      || result.action === "delete" && result.unchanged === true) continue;
    try {
      const receipt = habitId(result.receipt_id), operation = habitId(result.operation_id), action = habitActionId(result.action_id);
      habitId(result.habit_id);
      if (operation === action.toLowerCase() || seen.has(receipt)) continue;
      seen.add(receipt); publishHabit(context.userId); published++;
    } catch { /* Malformed/foreign data cannot refresh or mutate the module. */ }
  }
  const agendaSeen = new Set<string>();
  if (Array.isArray(results) && UUID.test(context.userId) && typeof context.sessionId === "string" && UUID.test(context.sessionId)) for (const result of results) {
    if (!context.isCurrent()) break;
    if (!record(result) || result.ok !== true || result.operation !== "verify_agenda_receipt" || result.scope !== "local" || result.evidence !== "agenda_action_receipt"
      || result.confirmed !== true || result.status !== "succeeded" || result.user_id !== context.userId || result.session_id !== context.sessionId
      || typeof result.action !== "string" || !isAgendaWriteAction(result.action) || result.unchanged !== undefined && typeof result.unchanged !== "boolean"
      || result.action !== "update" && result.unchanged === true) continue;
    try {
      const receipt = agendaId(result.receipt_id), operation = agendaId(result.operation_id), action = agendaActionId(result.action_id);
      agendaId(result.event_id);
      if (operation === action.toLowerCase() || agendaSeen.has(receipt)) continue;
      agendaSeen.add(receipt); publishAgenda(context.userId); published++;
    } catch { /* Malformed/foreign data cannot refresh or mutate the module. */ }
  }
  const bodyHealthSeen = new Set<string>();
  if (Array.isArray(results) && UUID.test(context.userId) && typeof context.sessionId === "string" && UUID.test(context.sessionId)) for (const result of results) {
    if (!context.isCurrent()) break;
    if (!record(result) || result.ok !== true || result.operation !== "verify_body_health_receipt" || result.scope !== "local" || result.evidence !== "body_health_action_receipt"
      || result.confirmed !== true || result.status !== "succeeded" || result.user_id !== context.userId || result.session_id !== context.sessionId
      || !["create","update","delete"].includes(String(result.action)) || !["body","health"].includes(String(result.resource))
      || result.unchanged !== undefined && typeof result.unchanged !== "boolean" || result.action !== "update" && result.unchanged === true) continue;
    try {
      const receipt = bodyHealthId(result.receipt_id), operation = bodyHealthId(result.operation_id), action = bodyHealthActionId(result.action_id);
      bodyHealthId(result.record_id);
      if (operation === action.toLowerCase() || bodyHealthSeen.has(receipt)) continue;
      bodyHealthSeen.add(receipt); notifyBodyHealthChanged(context.userId,result.resource as "body" | "health"); published++;
    } catch { /* Foreign/malformed receipts cannot refresh module state. */ }
  }
  const fitnessSeen = new Set<string>();
  if (Array.isArray(results) && UUID.test(context.userId) && typeof context.sessionId === "string" && UUID.test(context.sessionId)) for (const result of results) {
    if (!context.isCurrent()) break;
    if (!record(result) || result.ok !== true || result.operation !== "verify_fitness_receipt" || result.scope !== "local" || result.evidence !== "fitness_action_receipt" || result.confirmed !== true || result.status !== "succeeded" || result.user_id !== context.userId || result.session_id !== context.sessionId || typeof result.action !== "string" || !isFitnessWriteAction(result.action) || result.unchanged !== undefined && typeof result.unchanged !== "boolean") continue;
    try {
      const receipt = fitnessId(result.receipt_id), operation = fitnessId(result.operation_id), action = fitnessActionId(result.action_id);
      if (result.action === "save_profile") { if (result.plan_id != null) continue; } else fitnessId(result.plan_id);
      if (operation === action.toLowerCase() || fitnessSeen.has(receipt)) continue;
      fitnessSeen.add(receipt); notifyFitnessChanged(); published++;
    } catch { /* Foreign or malformed receipts cannot refresh Fitness. */ }
  }
  return published;
}
